import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Spinner } from '@karnov/kui-react'
import { ReactNode, useCallback, type FC, type KeyboardEvent, PropsWithChildren } from 'react'

import { useTriggerEvent } from 'hooks/useTriggerEvent'

import { useIsAccordionOpen, useToggleAccordion } from 'stores/useAccordionsStateStore'
import { cn } from 'utils/cn'

type Props = PropsWithChildren<{
  badge?: ReactNode
  id: string
  isLoading?: boolean
  messageId?: string
  summary?: string
}>

export const DetailsAccordion: FC<Props> = ({ badge, children, id, isLoading, messageId, summary }) => {
  const isAccordionOpen = useIsAccordionOpen({ id, namespace: 'details' })

  const toggleAccordion = useToggleAccordion({ namespace: 'details' })

  const triggerEvent = useTriggerEvent()

  const handleToggle = useCallback(() => {
    triggerEvent({
      name: isAccordionOpen ? 'kai_conversation_accordion_closed' : 'kai_conversation_accordion_opened',
      properties: {
        messageId,
      },
    })

    toggleAccordion(id)
  }, [id, isAccordionOpen, messageId, toggleAccordion, triggerEvent])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      handleToggle()
    }
  }, [handleToggle, isLoading])

  const handleClickAccordionHeader = useCallback(() => {
    if (!isLoading) {
      handleToggle()
    }
  }, [handleToggle, isLoading])

  return (
    <section
      className={
        cn(
          'print:list-none duration-200 ease-in-out border-l border-r border-b border-default px-4 first-of-type:mt-4 first-of-type:border-t first-of-type:rounded-t-lg last-of-type:rounded-b-lg',
          {
            'cursor-pointer': !isLoading,
            'cursor-default': isLoading,
          },
        )
      }

    >
      <div
        className="flex min-h-14 items-center"
        onPointerDown={handleClickAccordionHeader}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="mr-2 flex flex-1 justify-between">
          <div className={cn('font-medium', {
            'text-subdued': isLoading,
          })}
          >
            {summary}
          </div>
          <div>{badge}</div>
        </div>
        <div className="ml-1 w-5">
          {isLoading
            ? <div role="progressbar"><Spinner /></div>
            : <FontAwesomeIcon className="print:hidden" icon={isAccordionOpen ? faMinus : faPlus} />}
        </div>
      </div>

      <div className={cn('py-3 [&>*:first-child]:mt-0 print:block', { hidden: !isAccordionOpen })}>{children}</div>
    </section>
  )
}
